import { Link } from "react-router-dom";
import Header from "@/components/Header";
import { useLang } from "@/contexts/LangContext";
import { ArrowRight, BookOpen, CheckCircle2, AlertOctagon, Users } from "lucide-react";

const PHASE_KEYS = [
  { key: "phase_1", body: "docs_phase_1_d" },
  { key: "phase_2", body: "docs_phase_2_d" },
  { key: "phase_3", body: "docs_phase_3_d" },
  { key: "phase_4", body: "docs_phase_4_d" },
  { key: "phase_5", body: "docs_phase_5_d" },
];

const GLOSSARY_KEYS = [
  { term: "docs_term_sophistication", def: "docs_term_sophistication_d" },
  { term: "docs_term_framing", def: "docs_term_framing_d" },
  { term: "docs_term_mechanism", def: "docs_term_mechanism_d" },
  { term: "docs_term_objective", def: "docs_term_objective_d" },
  { term: "docs_term_narrative", def: "docs_term_narrative_d" },
  { term: "docs_term_derived", def: "docs_term_derived_d" },
];

const LIMITS_KEYS = [
  { t: "docs_not_1_t", d: "docs_not_1_d" },
  { t: "docs_not_2_t", d: "docs_not_2_d" },
  { t: "docs_not_3_t", d: "docs_not_3_d" },
];

const USE_CASE_KEYS = [
  { t: "docs_use_journalists_t", d: "docs_use_journalists_d" },
  { t: "docs_use_consultants_t", d: "docs_use_consultants_d" },
  { t: "docs_use_researchers_t", d: "docs_use_researchers_d" },
  { t: "docs_use_comms_t", d: "docs_use_comms_d" },
];

export default function Docs() {
  const { t } = useLang();
  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="docs-page">
      <Header />
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-12 md:py-20 space-y-16 md:space-y-24">
        {/* Hero */}
        <section className="grid md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-9">
            <div className="font-mono-tech text-xs tracking-[0.3em] uppercase text-[#D90429] mb-4 flex items-center gap-3">
              <span className="w-8 h-px bg-[#D90429]" />
              {t("docs_eyebrow")}
            </div>
            <h1
              data-testid="docs-title"
              className="font-serif-display text-5xl md:text-7xl font-bold tracking-tighter leading-none mb-6"
            >
              {t("docs_title")}
            </h1>
            <p className="font-sans-body text-base md:text-lg text-[#404040] leading-relaxed max-w-3xl">
              {t("docs_lead")}
            </p>
          </div>
          <div className="md:col-span-3 border border-[#171717] bg-white p-5">
            <div className="flex items-center gap-2 mb-3 text-[#D90429]">
              <BookOpen className="w-4 h-4" />
              <span className="font-mono-tech text-[10px] tracking-[0.3em] uppercase">v1</span>
            </div>
            <div className="font-mono-tech text-[10px] tracking-widest text-[#A3A3A3] uppercase">
              GalascioDAS™
            </div>
            <div className="font-serif-display text-base mt-1">
              Sistema desarrollado por Michael A. Galascio
            </div>
          </div>
        </section>

        {/* What it is */}
        <section className="grid md:grid-cols-12 gap-8" data-testid="docs-what">
          <div className="md:col-span-4">
            <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#D90429]">
              {t("docs_what_eyebrow")}
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl font-bold leading-tight mt-2">
              {t("docs_what_title")}
            </h2>
          </div>
          <div className="md:col-span-8">
            <p className="font-sans-body text-base text-[#262626] leading-relaxed">
              {t("docs_what_body")}
            </p>
            <div className="grid md:grid-cols-2 gap-4 mt-6">
              {[
                ["disc_1_t", "disc_1_d"],
                ["disc_2_t", "disc_2_d"],
                ["disc_3_t", "disc_3_d"],
                ["disc_4_t", "disc_4_d"],
              ].map(([h, d]) => (
                <div key={h} className="border-l-2 border-[#D90429] pl-4 py-2">
                  <div className="font-mono-tech text-[10px] tracking-widest uppercase text-[#0A0A0A]">
                    {t(h)}
                  </div>
                  <div className="font-sans-body text-sm text-[#525252] mt-1">{t(d)}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Protocol */}
        <section data-testid="docs-protocol">
          <div className="grid md:grid-cols-12 gap-8 mb-10">
            <div className="md:col-span-4">
              <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#D90429]">
                {t("docs_protocol_eyebrow")}
              </div>
              <h2 className="font-serif-display text-3xl md:text-4xl font-bold leading-tight mt-2">
                {t("docs_protocol_title")}
              </h2>
            </div>
          </div>
          <ol className="grid md:grid-cols-2 gap-px bg-[#171717] border border-[#171717]">
            {PHASE_KEYS.map((p, i) => (
              <li
                key={p.key}
                data-testid={`docs-phase-${i + 1}`}
                className={`bg-white p-6 md:p-8 ${i === PHASE_KEYS.length - 1 ? "md:col-span-2" : ""}`}
              >
                <div className="flex items-baseline gap-3 mb-3">
                  <span className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">
                    FASE
                  </span>
                  <span className="font-serif-display text-6xl font-bold leading-none text-[#D90429]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="font-serif-display text-xl font-bold leading-tight mb-2">
                  {t(p.key)}
                </h3>
                <p className="font-sans-body text-sm text-[#404040] leading-relaxed">
                  {t(p.body)}
                </p>
              </li>
            ))}
          </ol>
        </section>

        {/* Glossary */}
        <section data-testid="docs-glossary">
          <div className="grid md:grid-cols-12 gap-8 mb-8">
            <div className="md:col-span-4">
              <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#D90429]">
                {t("docs_glossary_eyebrow")}
              </div>
              <h2 className="font-serif-display text-3xl md:text-4xl font-bold leading-tight mt-2">
                {t("docs_glossary_title")}
              </h2>
            </div>
          </div>
          <dl className="grid md:grid-cols-2 gap-x-12 gap-y-8">
            {GLOSSARY_KEYS.map(({ term, def }) => (
              <div key={term} data-testid={`docs-glossary-${term}`} className="border-b border-[#E5E5E5] pb-5">
                <dt className="font-serif-display text-xl font-bold mb-2">{t(term)}</dt>
                <dd className="font-sans-body text-sm text-[#525252] leading-relaxed">
                  {t(def)}
                </dd>
              </div>
            ))}
          </dl>
        </section>

        {/* Limits */}
        <section data-testid="docs-limits" className="bg-[#0A0A0A] text-white p-8 md:p-12 -mx-6 md:-mx-10">
          <div className="grid md:grid-cols-12 gap-8 mb-8">
            <div className="md:col-span-4">
              <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#D90429] flex items-center gap-2">
                <AlertOctagon className="w-3.5 h-3.5" />
                {t("docs_not_eyebrow")}
              </div>
              <h2 className="font-serif-display text-3xl md:text-4xl font-bold leading-tight mt-2">
                {t("docs_not_title")}
              </h2>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {LIMITS_KEYS.map((l, i) => (
              <div key={l.t} data-testid={`docs-limit-${i + 1}`} className="border border-white/20 p-5">
                <div className="font-serif-display text-xl font-bold mb-3 leading-snug">
                  {t(l.t)}
                </div>
                <div className="font-sans-body text-sm text-white/70 leading-relaxed">
                  {t(l.d)}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Use cases */}
        <section data-testid="docs-use-cases">
          <div className="grid md:grid-cols-12 gap-8 mb-8">
            <div className="md:col-span-4">
              <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#D90429] flex items-center gap-2">
                <Users className="w-3.5 h-3.5" />
                {t("docs_use_eyebrow")}
              </div>
              <h2 className="font-serif-display text-3xl md:text-4xl font-bold leading-tight mt-2">
                {t("docs_use_title")}
              </h2>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#171717] border border-[#171717]">
            {USE_CASE_KEYS.map((u, i) => (
              <div
                key={u.t}
                data-testid={`docs-use-${i + 1}`}
                className="bg-white p-6"
              >
                <div className="flex items-center gap-2 text-[#D90429] mb-3">
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="font-mono-tech text-[10px] tracking-widest uppercase">
                    #{i + 1}
                  </span>
                </div>
                <div className="font-serif-display text-xl font-bold leading-tight mb-2">
                  {t(u.t)}
                </div>
                <div className="font-sans-body text-sm text-[#525252] leading-relaxed">
                  {t(u.d)}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section data-testid="docs-cta" className="border-t-2 border-[#0A0A0A] pt-12">
          <div className="flex flex-wrap items-center justify-between gap-6">
            <h2 className="font-serif-display text-3xl md:text-5xl font-bold tracking-tight leading-none max-w-xl">
              {t("hero_title")}
            </h2>
            <div className="flex flex-wrap gap-3">
              <Link
                data-testid="docs-cta-try"
                to="/register"
                className="group bg-[#D90429] text-white px-8 py-4 font-mono-tech text-xs uppercase tracking-[0.25em] border-2 border-[#D90429] hover:bg-[#0A0A0A] hover:border-[#0A0A0A] transition-colors inline-flex items-center gap-3"
              >
                {t("docs_cta_try")}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                data-testid="docs-cta-sample"
                to="/register?sample=1"
                className="px-8 py-4 font-mono-tech text-xs uppercase tracking-[0.25em] border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors"
              >
                {t("docs_cta_sample")}
              </Link>
            </div>
          </div>
          <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mt-8">
            🔬 GalascioDAS™ · {t("footer_signature")}
          </div>
        </section>
      </div>
    </div>
  );
}
